import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { provideMomentDateAdapter } from '@angular/material-moment-adapter';
import { MatCardModule } from '@angular/material/card';
import {
  DateRange,
  DefaultMatCalendarRangeStrategy,
  MAT_DATE_RANGE_SELECTION_STRATEGY,
  MatDatepickerModule,
} from '@angular/material/datepicker';
import moment, { Moment } from 'moment';
import { MatButtonModule } from '@angular/material/button';
import { CommonModule } from '@angular/common';

import { DateRangeCalendarOutput, DateRangeSelectorModel, PreDefinedDate } from './date-range-calendar.interface';

@Component({
  selector: 'app-date-range-calendar',
  templateUrl: './date-range-calendar.component.html',
  styleUrls: ['./date-range-calendar.component.scss'],
  standalone: true,
  imports: [CommonModule, MatCardModule, MatDatepickerModule, MatButtonModule],
  providers: [
    {
      provide: MAT_DATE_RANGE_SELECTION_STRATEGY,
      useClass: DefaultMatCalendarRangeStrategy,
    },
    provideMomentDateAdapter(),
  ],
})
export class DateRangeCalendarComponent implements OnInit {
  @Output() onCalenderChanged = new EventEmitter<DateRangeCalendarOutput>();
  @Input() maxDate: Moment | null;
  @Input() rangeValue: DateRangeSelectorModel | null = null;

  ngOnInit(): void {
    if (this.rangeValue) {
      this._dateRange = new DateRange(this.rangeValue.startDate, this.rangeValue.endDate);
    }
  }

  static DEFAULT_DATE_RANGE: DateRangeCalendarOutput = {
    label: 'Last 7 days',
    dateRange: {
      startDate: moment().subtract(6, 'days'),
      endDate: moment(),
    },
  };

  protected preDefinedDates: PreDefinedDate[] = [
    {
      label: 'Today',
      dateRange: {
        startDate: moment(),
        endDate: moment(),
      },
    },
    {
      label: 'Last 7 days',
      dateRange: {
        startDate: moment().subtract(6, 'days'),
        endDate: moment(),
      },
    },
    {
      label: 'Last 30 days',
      dateRange: {
        startDate: moment().subtract(29, 'days'),
        endDate: moment(),
      },
    },
  ];

  private _dateRange: DateRange<Moment> = new DateRange(moment(), moment());

  get dateRange(): DateRange<Moment> {
    return this._dateRange;
  }

  set range(range: DateRangeSelectorModel) {
    this._dateRange = new DateRange(range.startDate, range.endDate);
  }

  onSelectedChange(event: Moment | null): void {
    const selectedDate = this.createDateModel(event);

    if (!this.dateRange?.start) {
      this.range = this.createRangeModel(selectedDate);
      return;
    }

    const selectedDateIsRevered = moment(this.dateRange.start).isAfter(moment(selectedDate));
    if (selectedDateIsRevered) {
      this.range = this.createRangeModel(selectedDate);
      return;
    }

    const isStartDateLater = moment(this.dateRange.start).isAfter(moment(this.dateRange.end));

    if (isStartDateLater || this.dateRange?.end) {
      this.range = this.createRangeModel(selectedDate);
      return;
    }

    this.range = this.createRangeModel(this.dateRange.start, selectedDate);

    this.emitNewValue();
  }

  onPreDefinedDateSelect(preDefinedDate: PreDefinedDate): void {
    this.range = this.createRangeModel(preDefinedDate.dateRange.startDate, preDefinedDate.dateRange.endDate);

    this.emitNewValue(preDefinedDate.label);
  }

  private createDateModel(event: Moment | null): Moment | null {
    return moment(event);
  }

  private createRangeModel(startDate: Moment | null, endDate: Moment | null = null): DateRangeSelectorModel {
    return { startDate, endDate };
  }

  private emitNewValue(label?: string): void {
    const range = this.createRangeModel(this.dateRange.start, this.dateRange.end);

    const preparedDateRage = {
      startDate: moment(range.startDate).startOf('day').utc(),
      endDate: moment(range.endDate).endOf('day').utc(),
    };

    this.onCalenderChanged.emit({
      label: label ?? moment(range.startDate).format('DD.MM') + ' - ' + moment(range.endDate).format('DD.MM'),
      dateRange: preparedDateRage,
    });
  }
}
